<template>
  <section v-if="!isInitialized || featuredBooks.length > 0"
    class="pt-12 sm:pt-16 pb-12 sm:pb-16 px-4 transition-all duration-1000 ease-in-out"
    :style="{ background: backgroundGradient }">
    <div class="max-w-7xl mx-auto">
      <div class="relative px-4 sm:px-6 md:px-8 lg:px-12">
        <button v-if="isInitialized && featuredBooks.length > 0" @click="scrollLeft" :disabled="!canScrollLeft"
          class="absolute left-0 top-1/2 -translate-y-1/2 z-40 bg-white rounded-full p-2 md:p-3 shadow-lg hover:bg-creme-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto"
          aria-label="Scroll left">
          <svg class="w-5 h-5 md:w-6 md:h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div v-if="!isInitialized" class="text-center py-16">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>

        <div v-else-if="featuredBooks.length > 0" ref="sliderContainer"
          class="overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory relative z-0"
          @scroll="updateScrollState" @mouseenter="pauseAutoPlay" @mouseleave="startAutoPlay"
          @touchstart="pauseAutoPlay">
          <div class="flex pb-4">
            <div v-for="book in featuredBooks" :key="book.id"
              class="flex-shrink-0 snap-start flex items-center min-h-[400px] md:min-h-[500px]"
              :style="{ width: containerWidth + 'px' }">
              <div class="flex flex-col md:flex-row gap-6 md:gap-8 items-center max-w-4xl mx-auto px-4 w-full">
                <div class="flex-shrink-0 w-32 sm:w-40 md:w-48 lg:w-64 flex items-center justify-center">
                  <a :href="`/book/${book.id}`" class="block w-full">
                    <div :style="getBookImageStyle(book)"
                      class="bg-gray-200 rounded-none overflow-hidden hover:shadow-lg transition-shadow cursor-pointer mx-auto">
                      <div v-if="book.coverImage" class="w-full h-full">
                        <img :src="book.coverImage" :alt="book.title" class="w-full h-auto min-h-full object-cover" />
                      </div>
                      <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
                        <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                    </div>
                  </a>
                </div>

                <div class="flex-1 text-center md:text-left">
                  <a :href="`/book/${book.id}`" class="block">
                    <h3
                      class="text-xl sm:text-2xl md:text-3xl font-marketing font-bold text-primary mb-2 hover:text-accent transition-colors"
                      data-no-translate>
                      {{ book.title }}
                    </h3>
                  </a>
                  <a :href="`/author/${encodeURIComponent(book.author)}`" class="block mb-3 md:mb-4">
                    <p class="text-base sm:text-lg text-gray-700 font-body hover:text-accent transition-colors"
                      data-no-translate>
                      {{ book.author }}
                    </p>
                  </a>
                  <p
                    class="text-sm sm:text-base text-gray-600 font-body mb-4 md:mb-6 leading-relaxed max-w-2xl mx-auto md:mx-0 line-clamp-4 break-words">
                    {{ book.description }}
                  </p>
                  <div class="flex justify-center md:justify-start">
                    <a :href="`/book/${book.id}`"
                      class="inline-block bg-accent text-white px-6 sm:px-8 py-2 sm:py-3 rounded-md hover:bg-green-700 transition-colors font-marketing font-medium text-sm sm:text-base">
                      Buy Now
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button v-if="isInitialized && featuredBooks.length > 0" @click="scrollRight" :disabled="!canScrollRight"
          class="absolute right-0 top-1/2 -translate-y-1/2 z-40 bg-white rounded-full p-2 md:p-3 shadow-lg hover:bg-creme-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto"
          aria-label="Scroll right">
          <svg class="w-5 h-5 md:w-6 md:h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div v-else-if="isInitialized && featuredBooks.length === 0" class="text-center py-16">
          <p class="text-lg text-gray-600 font-marketing">{{ emptyMessage }}</p>
        </div>
      </div>

      <div v-if="featuredBooks.length > 0" class="flex flex-col items-center mt-8 gap-4">
        <div class="flex items-center gap-2">
          <span class="text-sm font-marketing text-primary">{{ currentBookIndex + 1 }} / {{ featuredBooks.length
          }}</span>
        </div>
        <div class="flex items-center gap-2">
          <button v-for="(book, index) in featuredBooks" :key="book.id" @click="goToSlide(index)" :class="[
            'w-2 h-2 rounded-full transition-all duration-300',
            currentBookIndex === index
              ? 'bg-accent w-8'
              : 'bg-gray-300 hover:bg-gray-400'
          ]" :aria-label="`Go to slide ${index + 1}`" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { extractDominantColors, createGradient } from '../utils/colors'
import { getFeaturedBooks } from '../utils/books'
import { useBookImageStyle } from '../composables/useBookImageStyle'
import { useEmptyMessages } from '../composables/useEmptyMessages'
import { useBookDescriptionTranslation } from '../composables/useBookDescriptionTranslation'
import { translationService } from '../services/translation'

const { getEmptyMessage } = useEmptyMessages()
const emptyMessage = computed(() => getEmptyMessage('slider') || 'No books found for the slider.')

const { getBookImageStyle: getBookImageStyleUtil } = useBookImageStyle()
const { translateDescriptions } = useBookDescriptionTranslation()

const sliderContainer = ref(null)
const containerWidth = ref(0)
const canScrollLeft = ref(false)
const canScrollRight = ref(true)
const backgroundGradient = ref('linear-gradient(135deg, #EFE7DD 0%, #FFF5EA 100%)')
const bookColors = ref({})
const bookGradients = ref({})
const currentBookIndex = ref(0)
const autoPlayInterval = ref(null)
const isUserInteracting = ref(false)
const colorsLoaded = ref(false)
const isInitialized = ref(false)

const featuredBooks = ref([])
// Keep the raw (untranslated) books so we can re-translate when the language changes
const originalBooks = ref([])

const updateScrollState = () => {
  if (!sliderContainer.value) return

  const { scrollLeft, scrollWidth, clientWidth } = sliderContainer.value
  containerWidth.value = clientWidth
  canScrollLeft.value = scrollLeft > 0
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 10

  const newIndex = Math.round(scrollLeft / clientWidth)
  if (newIndex !== currentBookIndex.value && newIndex >= 0 && newIndex < featuredBooks.value.length) {
    currentBookIndex.value = newIndex
    updateBackground()
  }
}

const updateBackground = () => {
  const currentBook = featuredBooks.value[currentBookIndex.value]
  if (currentBook && bookGradients.value[currentBook.id]) {
    backgroundGradient.value = bookGradients.value[currentBook.id]
  }
}

const extractColorsForBook = async (book) => {
  if (!book.coverImage) {
    bookGradients.value[book.id] = 'linear-gradient(135deg, #EFE7DD 0%, #FFF5EA 100%)'
    return
  }

  try {
    const colors = await extractDominantColors(book.coverImage, 3)
    bookColors.value[book.id] = colors
    bookGradients.value[book.id] = createGradient(colors)
  } catch (error) {
    console.warn(`Failed to extract colors for book ${book.id}:`, error)
    bookGradients.value[book.id] = 'linear-gradient(135deg, #EFE7DD 0%, #FFF5EA 100%)'
  }
}

const scrollLeft = () => {
  if (!sliderContainer.value || !isInitialized.value) return
  const slideWidth = sliderContainer.value.clientWidth
  sliderContainer.value.scrollBy({ left: -slideWidth, behavior: 'smooth' })
  pauseAutoPlay()
}

const scrollRight = () => {
  if (!sliderContainer.value || !isInitialized.value) return
  const slideWidth = sliderContainer.value.clientWidth
  sliderContainer.value.scrollBy({ left: slideWidth, behavior: 'smooth' })
  pauseAutoPlay()
}

const goToSlide = (index) => {
  if (!sliderContainer.value) return
  const slideWidth = sliderContainer.value.clientWidth
  sliderContainer.value.scrollTo({ left: slideWidth * index, behavior: 'smooth' })
  pauseAutoPlay()
}

const startAutoPlay = () => {
  if (autoPlayInterval.value) return
  autoPlayInterval.value = setInterval(() => {
    if (!isUserInteracting.value && sliderContainer.value) {
      const { scrollLeft, clientWidth } = sliderContainer.value
      const slideWidth = clientWidth
      const currentSlide = Math.round(scrollLeft / slideWidth)
      const nextSlide = currentSlide + 1

      if (nextSlide >= featuredBooks.value.length) {
        sliderContainer.value.scrollTo({ left: 0, behavior: 'smooth' })
      } else {
        sliderContainer.value.scrollTo({ left: slideWidth * nextSlide, behavior: 'smooth' })
      }
    }
  }, 5000)
}

const pauseAutoPlay = () => {
  isUserInteracting.value = true
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
    autoPlayInterval.value = null
  }
  setTimeout(() => {
    isUserInteracting.value = false
    startAutoPlay()
  }, 10000)
}

const handleResize = () => {
  if (sliderContainer.value) {
    containerWidth.value = sliderContainer.value.clientWidth
  }
  updateScrollState()
}

onMounted(async () => {
  await nextTick()

  const rawBooks = await getFeaturedBooks()
  originalBooks.value = rawBooks

  // Smart-translate descriptions: detect source language per book and only
  // translate when the description language differs from the user's language.
  featuredBooks.value = await translateDescriptions(rawBooks, translationService.getLanguage())

  await nextTick()

  isInitialized.value = true

  if (featuredBooks.value.length > 0) {
    await nextTick()

    if (sliderContainer.value) {
      containerWidth.value = sliderContainer.value.clientWidth
      sliderContainer.value.scrollTo({ left: 0, behavior: 'auto' })
      await nextTick()
      updateScrollState()
    }
  }

  window.addEventListener('resize', handleResize)
  window.addEventListener('language-changed', handleLanguageChange)

  const colorPromises = featuredBooks.value.map(book => extractColorsForBook(book))
  await Promise.all(colorPromises)
  colorsLoaded.value = true

  if (featuredBooks.value.length > 0) {
    updateBackground()
  }

  startAutoPlay()
})

const handleLanguageChange = async (event) => {
  const newLang = event?.detail?.language || translationService.getLanguage()
  featuredBooks.value = await translateDescriptions(originalBooks.value, newLang)
}

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('language-changed', handleLanguageChange)
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
  }
})

const getBookImageStyle = (book) => {
  return getBookImageStyleUtil(book)
}
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

button[aria-label="Scroll left"],
button[aria-label="Scroll right"] {
  position: absolute;
  pointer-events: auto !important;
  z-index: 40 !important;
}
</style>
